import PageBanner from "../components/ui/PageBanner";
import "./About.css";
import { useMemo, useState } from "react";

export default function FaqsComplaints({ setPage }) {
  const FAQ = useMemo(
    () => [
      {
        title: "General Questions",
        items: [
          {
            q: "What payment options do you accept?",
            a: "We accept cash and common digital payment methods. Payment options can vary by service, so please confirm at reception when booking your appointment.",
          },
          {
            q: "Do you accept insurance?",
            a: "Insurance acceptance depends on your provider and plan. Please bring your insurance card and CNIC/ID and our team will guide you on coverage and approvals.",
          },
          {
            q: "Do I need a referral from my doctor?",
            a: "Most services do not require a referral. Some specialized imaging or procedures may require a doctor’s advice for the best preparation and safety.",
          },
          {
            q: "How long do results take?",
            a: "Result times vary by test type. Routine lab tests are often available the same day, while imaging and specialized tests may take longer depending on reporting requirements.",
          },
        ],
      },
      {
        title: "Preparation & Procedures",
        items: [
          {
            q: "What should I bring to my appointment?",
            a: "Bring your CNIC/ID, previous reports (if any), current medication list, and any referral/prescription from your doctor. If using insurance, bring your insurance card as well.",
          },
          {
            q: "Do I need to fast before my blood test?",
            a: "Some tests require fasting (commonly 8–12 hours). If your doctor requested fasting, please follow their instructions; otherwise, confirm with reception before your visit.",
          },
          {
            q: "What should I expect during an MRI?",
            a: "MRI is painless and uses magnets (no radiation). You’ll lie still while the scanner makes loud sounds. Tell staff about any metal implants or claustrophobia before the scan.",
          },
          {
            q: "Can I drive after my procedure?",
            a: "After most routine tests you can drive. If you receive sedation, certain medications, or feel dizzy/unwell, arrange a companion to take you home.",
          },
        ],
      },
      {
        title: "Results & Reports",
        items: [
          {
            q: "How will I receive my results?",
            a: "You may receive results at the facility and/or be contacted when they’re ready, depending on the test type. Our staff will guide you at the time of sample collection or imaging.",
          },
          {
            q: "Can I access my results online?",
            a: "Online access depends on the reporting system for your test. If available, our team can share the correct method and details at the time of your visit.",
          },
          {
            q: "What if I don't understand my results?",
            a: "Bring your report to your doctor for interpretation, or contact us for guidance on the appropriate specialist to consult based on your results.",
          },
          {
            q: "Can I get copies of my results for other doctors?",
            a: "Yes. You can request printed or digital copies from reception. Please keep your reference number and ID for verification.",
          },
        ],
      },
      {
        title: "Scheduling & Appointments",
        items: [
          {
            q: "How far in advance should I schedule?",
            a: "For routine consultations, booking a day or two ahead is usually enough. For specialist appointments or specific tests, we recommend booking earlier to secure your preferred time.",
          },
          {
            q: "Do you accept walk-ins?",
            a: "Walk-ins may be accepted based on availability. To reduce waiting time, we recommend booking in advance, especially for specialists and imaging services.",
          },
          {
            q: "What is your cancellation policy?",
            a: "If you need to cancel or change your appointment, please inform us as early as possible so we can offer the slot to another patient.",
          },
          {
            q: "Can I reschedule my appointment online?",
            a: "Online rescheduling depends on the appointment type. The quickest option is to contact our team and we’ll reschedule you to the next available slot.",
          },
        ],
      },
    ],
    []
  );

  const [openId, setOpenId] = useState(null);

  return (
    <div className="about-page">
      <PageBanner title="FAQs & Complaints" crumbs={["Home", "About", "FAQs & Complaints"]} />

      <section className="section">
        <div className="section-inner">
          <div className="about__section-head">
            <p className="section-label">Frequently Asked Questions</p>
            <h3 className="section-title">Find answers to common questions about our services and procedures</h3>
            <p className="section-text">
              Find answers to common questions about our services, procedures, and policies. Can&apos;t find what
              you&apos;re looking for? Contact us directly.
            </p>
          </div>

          <div className="faq__grid">
            {FAQ.map((group) => (
              <div key={group.title} className="card faq__card">
                <h3 className="section-title" style={{ marginBottom: 10 }}>
                  {group.title}
                </h3>
                <div className="faq__accordion">
                  {group.items.map((item, idx) => {
                    const id = `${group.title}-${idx}`.replace(/\s+/g, "-").toLowerCase();
                    const isOpen = openId === id;
                    return (
                      <div key={id} className={`faq__qa ${isOpen ? "faq__qa--open" : ""}`}>
                        <button
                          type="button"
                          className="faq__q"
                          aria-expanded={isOpen}
                          aria-controls={`${id}-panel`}
                          onClick={() => setOpenId((prev) => (prev === id ? null : id))}
                        >
                          <span>{item.q}</span>
                          <span className="faq__chev" aria-hidden="true">
                            +
                          </span>
                        </button>
                        <div id={`${id}-panel`} className="faq__panel" role="region">
                          <div className="faq__panel-inner">
                            <p className="faq__a">{item.a}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="faq__support">
            <div className="card faq__support-card">
              <div className="faq__support-title">Call Us</div>
              <div className="faq__support-sub">Speak with our knowledgeable staff</div>
              <div className="faq__support-value">0323-5159616</div>
              <button type="button" className="btn-primary" onClick={() => setPage?.("contact")}>
                Call Patient Care
              </button>
            </div>

            <div className="card faq__support-card">
              <div className="faq__support-title">Live Chat</div>
              <div className="faq__support-sub">Get instant answers online</div>
              <button type="button" className="btn-primary" onClick={() => setPage?.("contact")}>
                Start Chat
              </button>
            </div>

            <div className="card faq__support-card">
              <div className="faq__support-title">Help Center</div>
              <div className="faq__support-sub">Browse our knowledge base</div>
              <button type="button" className="btn-primary" onClick={() => setPage?.("contact")}>
                Visit Help Center
              </button>
            </div>
          </div>

          <div className="card faq__bottom">
            <div className="faq__bottom-head">
              <h3 className="section-title" style={{ margin: 0 }}>
                Still Have Questions?
              </h3>
              <p className="section-text" style={{ margin: "8px 0 0" }}>
                Our patient care team is here to help. We&apos;re committed to making your diagnostic experience as smooth
                and comfortable as possible.
              </p>
            </div>

            <div className="about__actions" style={{ justifyContent: "flex-start" }}>
              <button type="button" className="btn-primary" onClick={() => setPage?.("contact")}>
                Call Patient Care
              </button>
              <button type="button" className="btn-outline" onClick={() => setPage?.("contact")}>
                Schedule a Consultation
              </button>
            </div>

            <div className="faq__hours">
              <div>Patient Care Hours: 24/7 Hours</div>
              <div>Emergency Line: Available 24/7 for urgent results</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
